import React, { useMemo, useState } from 'react';
import {
  Badge,
  BadgeText,
  Box,
  Button,
  Card,
  Heading,
  HStack,
  Icon,
  Pressable,
  Text,
  VStack,
} from '@gluestack-ui/themed';
import { ChevronDown, ChevronRight, ChevronUp } from 'lucide-react-native';
import { useTranslation } from 'react-i18next';
import HtmlText from '@/common/components/HtmlText';
import { getOralExamQuestionTitle } from '@/common/components/OralExamQuestionText';
import type { OralExamSubEvaluationOutput } from '@/common/api/models/oralExamSubEvaluationOutput';
import type { Task } from '@/common/api/models/task';
import type { PerformanceLevelConfig } from '@/common/api/models/performanceLevelConfig';
import { useEvaluationGradingConfig } from '@/lecturer/EvaluationDesk/context/EvaluationGradingConfigContext';
import {
  bindOralExamSubEvaluationsToAskedIds,
  getOralExamQuestionIds,
  resolveOralExamQuestionTextFromTask,
} from '@/lecturer/EvaluationDesk/utils/evaluationFormMapping';
import OralExamRadar, {
  type RadarDimension,
} from '@/student/grade/components/parts/OralExamRadar';
import SubmissionDetailsModal from '@/student/grade/components/parts/SubmissionDetailsModal';
import {
  averageDimLevel,
  averageQuestionLevel,
} from '@/student/grade/components/parts/oralExamSectionUtils';

interface OralExamSectionProps {
  tasks: Task[];
}

const getLevelLabel = (
  levelId: string | null | undefined,
  performanceLevels: PerformanceLevelConfig[],
): string => {
  if (!levelId) return '';
  return performanceLevels.find((level) => level.id === levelId)?.label || levelId;
};

const LevelPill: React.FC<{
  level: string;
  performanceLevels: PerformanceLevelConfig[];
}> = ({ level, performanceLevels }) => (
  <Badge action="muted" variant="outline" size="sm">
    <BadgeText>{getLevelLabel(level, performanceLevels)}</BadgeText>
  </Badge>
);

interface QuestionRowProps {
  task: Task;
  questionIndex: number;
  subEvalId: string;
  dimGrades: Record<string, { level?: string } | undefined>;
  questionFeedback?: string | null;
  performanceLevels: PerformanceLevelConfig[];
  levelWeights: Record<string, number>;
  oralExamDimensionKeys: string[];
}

const QuestionRow: React.FC<QuestionRowProps> = ({
  task,
  questionIndex,
  subEvalId,
  dimGrades,
  questionFeedback,
  performanceLevels,
  levelWeights,
  oralExamDimensionKeys,
}) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  // The grade report names the question. The prompt itself stays on the exam.
  const questionText = resolveOralExamQuestionTextFromTask(task, subEvalId) ?? '';
  const questionTitle = getOralExamQuestionTitle(questionText)
    ?? questionText.split('\n')[0]?.trim()
    ?? '';
  const feedbackText = questionFeedback?.trim() ?? '';
  const questionLevel = averageQuestionLevel(dimGrades, levelWeights, performanceLevels);

  const ratedDimensions = useMemo(() => oralExamDimensionKeys
    .map((dimension) => {
      const level = dimGrades[dimension]?.level;
      if (!level || level === 'n/a') return null;
      return { dimension, level };
    })
    .filter((entry): entry is { dimension: string; level: string } => entry !== null), [
    oralExamDimensionKeys,
    dimGrades,
  ]);

  const hasBreakdown = ratedDimensions.length > 0;

  const rowContent = (
    <HStack space="md" alignItems="flex-start" py="$3">
      <Box w={20} pt="$0.5">
        <Text size="sm" color="$textSubtle" fontWeight="$semibold">
          {questionIndex + 1}
        </Text>
      </Box>

      <VStack flex={1} space="xs">
        <Text size="sm" color="$textHeading" fontWeight="medium">
          {questionTitle || t('grade.oralExam.questionFallback', { n: questionIndex + 1 })}
        </Text>

        {!!feedbackText && (
          <HtmlText
            content={feedbackText}
            fontSize={13}
            contentStyle={{}}
            numberOfLines={0}
            applySubmissionFormatting={false}
          />
        )}

        {expanded && hasBreakdown && (
          <Text size="xs" color="$textSubtle" mt="$1">
            {ratedDimensions.map(({ dimension, level }) => (
              `${t(`common.objective-dimensions.${dimension}`)} ${getLevelLabel(level, performanceLevels)}`
            )).join('  ·  ')}
          </Text>
        )}
      </VStack>

      <HStack space="xs" alignItems="center" pt="$0.5">
        {questionLevel && (
          <LevelPill level={questionLevel} performanceLevels={performanceLevels} />
        )}
        {hasBreakdown && (
          <Icon as={expanded ? ChevronUp : ChevronDown} size="sm" color="$textHint" />
        )}
      </HStack>
    </HStack>
  );

  if (!hasBreakdown) {
    return (
      <Box
        borderBottomWidth={1}
        borderBottomColor="$borderLight200"
        testID={`oral-exam-question-row-${questionIndex}`}
      >
        {rowContent}
      </Box>
    );
  }

  return (
    <Pressable
      onPress={() => setExpanded((value) => !value)}
      testID={`oral-exam-question-row-${questionIndex}`}
      borderBottomWidth={1}
      borderBottomColor="$borderLight200"
    >
      {rowContent}
    </Pressable>
  );
};

const OralExamTaskSection: React.FC<{
  task: Task;
  showTaskTitle: boolean;
  showDivider: boolean;
}> = ({ task, showTaskTitle, showDivider }) => {
  const { t } = useTranslation();
  const {
    oralExamDimensionKeys,
    levelWeights,
    performanceLevels,
  } = useEvaluationGradingConfig();

  const oralExamEvaluation = task.submission?.oralExamEvaluation;
  const submissionFeedback = task.submission?.feedback?.text?.trim() ?? '';
  const overallText = oralExamEvaluation?.overallFeedback?.text?.trim() ?? '';
  const transcript = task.submission?.text?.trim() ?? '';
  const [showTranscript, setShowTranscript] = useState(false);
  // For oral exams, the autograded overall feedback is the authoritative summary.
  // Fall back to the lecturer's submission feedback only when no overall is present.
  const primaryAssessment = overallText || submissionFeedback;

  const questionRows = useMemo(() => {
    const evalList = oralExamEvaluation?.subEvaluations ?? [];
    const storedByKey = Object.fromEntries(evalList.map((row) => [row.id, row]));
    const askedIds = getOralExamQuestionIds(task);
    const boundSubEvaluations = bindOralExamSubEvaluationsToAskedIds(task, askedIds, storedByKey);
    return askedIds.map((id): OralExamSubEvaluationOutput => {
      const sub = boundSubEvaluations.get(id);
      return {
        id,
        dimGrades: sub?.dimGrades ?? {},
        questionFeedback: sub?.questionFeedback ?? null,
      };
    });
  }, [oralExamEvaluation?.subEvaluations, task]);

  const radarDimensions = useMemo<RadarDimension[]>(() => {
    if (questionRows.length === 0) return [];
    return oralExamDimensionKeys.map((dimension) => ({
      key: dimension,
      label: t(`common.objective-dimensions.${dimension}`),
      level: averageDimLevel(
        questionRows,
        dimension,
        levelWeights,
        performanceLevels,
      ),
    }));
  }, [questionRows, oralExamDimensionKeys, levelWeights, performanceLevels, t]);

  const hasAnyRatedDimension = radarDimensions.some((d) => !!d.level);

  return (
    <VStack
      space="3xl"
      pt={showDivider ? '$4' : '$0'}
      borderTopWidth={showDivider ? 1 : 0}
      borderTopColor="$borderLight200"
    >
      {showTaskTitle && <Heading size="sm">{task.title}</Heading>}

      {!!primaryAssessment && (
        <VStack space="sm">
          <Text size="xs" color="$textSubtle" textTransform="uppercase" letterSpacing={0.5}>
            {t('grade.oralExam.overallAssessment')}
          </Text>
          <HtmlText
            content={primaryAssessment}
            fontSize={15}
            contentStyle={{}}
            numberOfLines={0}
            applySubmissionFormatting={false}
          />
        </VStack>
      )}

      {hasAnyRatedDimension && (
        <VStack space="md">
          <Text size="xs" color="$textSubtle" textTransform="uppercase" letterSpacing={0.5}>
            {t('grade.oralExam.performanceByArea')}
          </Text>
          <HStack
            space="2xl"
            alignItems="center"
            flexWrap="wrap"
            rowGap={16}
          >
            <OralExamRadar
              dimensions={radarDimensions}
              performanceLevels={performanceLevels}
              levelWeights={levelWeights}
            />
            <VStack flex={1} minWidth={260} space="md">
              {radarDimensions.map((dimension) => {
                const fullTitleKey = `grade.oralExam.dimensions.${dimension.key}.title`;
                const descriptionKey = `grade.oralExam.dimensions.${dimension.key}.description`;
                const fullTitle = t(fullTitleKey, { defaultValue: dimension.label });
                const description = t(descriptionKey, { defaultValue: '' });
                return (
                  <HStack
                    key={dimension.key}
                    alignItems="center"
                    space="md"
                    accessibilityLabel={dimension.level
                      ? `dim-${dimension.key}-level-${dimension.level}`
                      : `dim-${dimension.key}-level-unrated`}
                  >
                    <VStack flex={1} space="xs">
                      <Text size="sm" color="$textBody" fontWeight="medium">
                        {fullTitle}
                      </Text>
                      {!!description && (
                        <Text size="xs" color="$textSubtle">
                          {description}
                        </Text>
                      )}
                    </VStack>
                    <Box ml="auto">
                      {dimension.level ? (
                        <LevelPill level={dimension.level} performanceLevels={performanceLevels} />
                      ) : (
                        <Text size="xs" color="$textSubtle">
                          {t('grade.oralExam.noDimensionRating')}
                        </Text>
                      )}
                    </Box>
                  </HStack>
                );
              })}
            </VStack>
          </HStack>
        </VStack>
      )}

      {questionRows.length > 0 && (
        <VStack space="sm">
          <Text size="xs" color="$textSubtle" textTransform="uppercase" letterSpacing={0.5}>
            {t('grade.oralExam.questionByQuestion')}
          </Text>
          <VStack borderTopWidth={1} borderTopColor="$borderLight200">
            {questionRows.map((subEvaluation, index) => (
              <QuestionRow
                key={subEvaluation.id}
                task={task}
                questionIndex={index}
                subEvalId={subEvaluation.id}
                dimGrades={subEvaluation.dimGrades}
                questionFeedback={subEvaluation.questionFeedback}
                performanceLevels={performanceLevels}
                levelWeights={levelWeights}
                oralExamDimensionKeys={oralExamDimensionKeys}
              />
            ))}
          </VStack>
        </VStack>
      )}

      {!!transcript && (
        <Button
          variant="link"
          onPress={() => setShowTranscript(true)}
          alignSelf="flex-start"
          p="$0"
          testID={`oral-exam-view-transcript-${task.id}`}
        >
          <HStack space="xs" alignItems="center">
            <Text size="sm" color="$blue500">
              {t('grade.oralExam.viewTranscript')}
            </Text>
            <Icon as={ChevronRight} size="sm" color="$blue500" />
          </HStack>
        </Button>
      )}

      {!!transcript && (
        <SubmissionDetailsModal
          visible={showTranscript}
          onClose={() => setShowTranscript(false)}
          title={task.title}
          submissionText={transcript}
          submissionLabel={t('grade.oralExam.transcript')}
          noSubmissionLabel={t('grade.oralExam.noTranscript')}
          applySubmissionFormatting={false}
        />
      )}
    </VStack>
  );
};

const OralExamSection: React.FC<OralExamSectionProps> = ({ tasks }) => {
  const { t } = useTranslation();

  const oralExamTasks = tasks.filter((task) => (
    !!task.submission?.oralExamEvaluation
    || !!task.submission?.feedback?.text?.trim()
  ));

  if (oralExamTasks.length === 0) return null;

  return (
    <Card w="100%" p="$6" mb="$4">
      <VStack space="lg">
        <Heading size="md">{t('grade.oralExam.sectionTitle')}</Heading>
        {oralExamTasks.map((task, index) => (
          <OralExamTaskSection
            key={task.id}
            task={task}
            showTaskTitle={oralExamTasks.length > 1}
            showDivider={index > 0}
          />
        ))}
      </VStack>
    </Card>
  );
};

export default OralExamSection;
